import {
  Content,
  ContentAudience,
  DuplicationResultsResponse,
  UpdateProposalEventType,
  UpdateProposalRequest,
  UpdateProposalResponse,
  UpdateProposalContentIdMessage,
  UpdateProposalErrorMessage,
} from '@amzn/awscat-pattern-library-service-model';
import { createEventSource, FetchLike } from 'eventsource-client';
import JSONSerializer from '../common/JSONSerializer';
import { localLog } from '../loggers/Logger';

/**
 * A callback that returns a valid auth token, refreshing if necessary.
 * This keeps auth logic centralized in MplServiceClient rather than
 * duplicated here.
 */
export type GetTokenFn = () => Promise<string | undefined>;

/**
 * Creates a fetch wrapper that intercepts 401 responses, refreshes the
 * token via `getToken`, and retries the request once with the new token.
 * This mirrors the Axios interceptor pattern used by MplServiceClient
 * but works with the eventsource-client's `fetch` option.
 */
function createAuthFetch(getToken: GetTokenFn): FetchLike {
  let hasRetried = false;

  return async (url, init) => {
    const response = await fetch(url, init);

    if (response.status === 401 && !hasRetried) {
      hasRetried = true;
      localLog.debug('[updateProposal] Received 401, refreshing token and retrying...');
      const freshToken = await getToken();
      if (freshToken && init) {
        const retryHeaders = { ...init.headers, Authorization: `Bearer ${freshToken}` };
        return fetch(url, { ...init, headers: retryHeaders });
      }
    }

    return response;
  };
}

/**
 * Handles proposal creation/updating via SSE streaming.
 *
 * A built-in timeout (default 2 minutes) guards against hung connections
 * where the server stops sending events without closing the stream.
 * Callers can also pass an AbortSignal for external cancellation.
 *
 * Auth is handled reactively: the initial request uses the current token.
 * If the server responds with a 401, the provided `getToken` callback is
 * invoked to refresh the token and the request is retried once — matching
 * the same pattern as the Axios interceptor in MplServiceClient.
 */
export function updateProposal(
  endpoint: string,
  serializer: JSONSerializer,
  content: Content,
  contentAudience: ContentAudience,
  getToken: GetTokenFn,
  currentToken: string | undefined,
  contentId?: string,
  onDuplicationEvent?: (event: DuplicationResultsResponse) => void,
  onContentId?: (contentId: string, workflowId: number) => void,
  signal?: AbortSignal,
  timeoutMs: number = 2 * 60 * 1000,
): Promise<UpdateProposalResponse | null> {
  const request = UpdateProposalRequest.create(content, contentAudience, contentId);
  const serializedRequest = serializer.serialize(request);
  const url = `${endpoint}/proposal`;

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Accumulate results from mid-stream events so we can build the
    // final response when the server sends the DONE signal.
    let accumulatedContentId: string | undefined;
    let accumulatedDuplicationResults: DuplicationResultsResponse | undefined;

    const settle = (value: UpdateProposalResponse | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(value);
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      reject(error);
    };

    const es = createEventSource({
      url,
      method: 'POST',
      fetch: createAuthFetch(getToken),
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
        'x-auth-provider': 'catportal',
      },
      body: JSON.stringify(serializedRequest),
      onMessage: ({ data, event: eventType }) => {
        if (signal?.aborted) return;

        localLog.debug(
          { eventType, dataLength: data?.length },
          '[updateProposal] SSE event received',
        );

        // DONE and ERROR are terminal events
        if (eventType === UpdateProposalEventType.DONE) {
          const response = new UpdateProposalResponse();
          if (accumulatedContentId) {
            response.contentId = accumulatedContentId;
          }
          if (accumulatedDuplicationResults) {
            response.duplicationCheckResults = accumulatedDuplicationResults;
          }
          es.close();
          settle(response);
          return;
        }

        if (eventType === UpdateProposalEventType.ERROR) {
          let errorMessage = data || 'Server sent an error event';
          try {
            const parsed = JSON.parse(data) as Record<string, unknown>;
            const parsedErrorEvent = serializer.deserialize<UpdateProposalErrorMessage>(
              parsed,
              UpdateProposalErrorMessage,
            );
            if (parsedErrorEvent?.error) {
              errorMessage = parsedErrorEvent.error;
            }
            localLog.error({ parsedErrorEvent }, '[updateProposal] Error updating proposal');
          } catch (error) {
            localLog.error({ error }, '[updateProposal] Error parsing error event');
          }
          es.close();
          fail(new Error(errorMessage));
          return;
        }

        if (!data) return;

        // Content-ID event carries the content identifier mid-stream
        if (eventType === UpdateProposalEventType.CONTENT_ID_EVENT) {
          try {
            const parsed = JSON.parse(data) as Record<string, unknown>;

            const parsedContentIdEvent = serializer.deserialize<UpdateProposalContentIdMessage>(
              parsed,
              UpdateProposalContentIdMessage,
            );
            accumulatedContentId = parsedContentIdEvent.contentId;
            if (onContentId) {
              onContentId(parsedContentIdEvent.contentId, parsedContentIdEvent.workflowId);
            }
          } catch (error) {
            localLog.error({ error }, '[updateProposal] Error parsing content-id event');
          }
          return;
        }

        if (eventType === UpdateProposalEventType.DUPLICATE_EVENT) {
          try {
            const parsed = JSON.parse(data) as Record<string, unknown>;

            const duplicationEvent = serializer.deserialize<DuplicationResultsResponse>(
              parsed,
              DuplicationResultsResponse,
            );

            // Accumulate for the final response built on DONE.
            accumulatedDuplicationResults = duplicationEvent;

            if (onDuplicationEvent) {
              onDuplicationEvent(duplicationEvent);
            }
          } catch (error) {
            localLog.error({ error }, '[updateProposal] Error parsing SSE data');
          }
        }
      },
      onDisconnect: () => {
        es.close();
        if (!settled) {
          localLog.warn('[updateProposal] Stream disconnected without a terminal event');
          fail(new Error('SSE stream disconnected before receiving a terminal event'));
        }
      },
    });

    // Allow external cancellation via AbortSignal.
    // { once: true } ensures the listener is removed after firing, so it
    // won't leak if the AbortController outlives this call.
    signal?.addEventListener(
      'abort',
      () => {
        es.close();
        settle(null);
      },
      { once: true },
    );

    // Guard against hung connections where the server stops sending
    // events without closing the stream.
    timeoutId = setTimeout(() => {
      localLog.error('[updateProposal] Stream timed out');
      es.close();
      fail(new Error('Proposal SSE stream timed out'));
    }, timeoutMs);
  });
}
